export const MIN_SAFE_LUMINANCE = 80;

function waitForVideoFrame(
  video: HTMLVideoElement,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false;
    const listeners: Array<[string, EventListener]> = [];

    const cleanup = () => {
      listeners.forEach(([event, handler]) =>
        video.removeEventListener(event, handler),
      );
    };

    const settle = (ok: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ok);
    };

    listeners.push(['loadeddata', () => settle(true)]);
    listeners.push(['playing', () => settle(true)]);
    listeners.push(['error', () => settle(false)]);
    listeners.forEach(([event, handler]) =>
      video.addEventListener(event, handler),
    );

    if (video.readyState >= 2) {
      settle(true);
      return;
    }

    setTimeout(() => settle(false), timeoutMs);
  });
}

function computeLuminanceMean(imageData: ImageData): number {
  const { data } = imageData;
  if (data.length === 0) return 0;
  let sum = 0;
  // Rec. 601 luma: 0.299 R + 0.587 G + 0.114 B
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return sum / (data.length / 4);
}

/**
 * Mede a luminância média (Rec.601) de um frame capturado do stream.
 * Nunca lança: retorna `null` em caso de timeout, falha ou contexto 2D
 * indisponível. A chamada é silenciosa por design — não deve quebrar o
 * fluxo de preparação do exame.
 */
export async function measureStreamLuminance(
  stream: MediaStream,
  timeoutMs = 1500,
): Promise<number | null> {
  if (typeof document === 'undefined') return null;
  if (!stream.getVideoTracks().some(track => track.readyState === 'live')) {
    return null;
  }

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  try {
    try {
      await video.play();
    } catch {
      // ignore autoplay errors; we can still capture from readyState
    }

    const ready = await waitForVideoFrame(video, timeoutMs);
    if (!ready) return null;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    return computeLuminanceMean(imageData);
  } catch {
    return null;
  } finally {
    video.srcObject = null;
  }
}
